// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: calendar-driven presence. While the user's Outlook calendar is
// connected, automatically switch to the "In a meeting" preset for the
// duration of a calendar event and restore the previous status afterwards.
// A manual status change while auto-presence is active wins: automation backs
// off until the next event.
//
// Ownership lives on the user settings row (status_hold_source), not in local
// storage, so every device agrees on whether the current status is automatic.
// The server clears the field on any status change that does not name a source,
// which is what makes a manual change take the status back. Setting plain
// Available by hand would otherwise be retaken on the next tick, so when the
// hold is dropped while a meeting is live we remember that meeting's end and
// leave the status alone until then. Every device sees the hold drop, so they
// all back off together.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import type {KonvoCalendarEvent} from '@app/features/konvo_calendar/state/KonvoCalendar';
import KonvoCalendarBridge from '@app/features/konvo_calendar/state/KonvoCalendarBridge';
import KonvoCalendarPrefs from '@app/features/konvo_calendar/state/KonvoCalendarPrefs';
import KonvoUpNext from '@app/features/konvo_calendar/state/KonvoUpNext';
import {getLiveMeetingEnd, getNextMeetingBoundary} from '@app/features/konvo_calendar/utils/KonvoAutoPresenceTiming';
import KonvoConnections from '@app/features/konvo_connections/state/KonvoConnections';
import {getProtectedLocalStorage} from '@app/features/platform/state/ProtectedWebStorage';
import {http} from '@app/features/platform/transport/RestTransport';
import {KONVO_STATUS_PRESETS, presetCustomStatus} from '@app/features/presence/state/KonvoStatusPresets';
import {VisiblePoll} from '@app/features/ui/utils/VisiblePoll';
import * as UserSettingsCommands from '@app/features/user/commands/UserSettingsCommands';
import UserSettings from '@app/features/user/state/UserSettings';
import Window from '@app/features/window/state/Window';
import {StatusTypes} from '@fluxer/constants/src/StatusConstants';
import {reaction} from 'mobx';

const CHECK_INTERVAL_MS = 30 * 1000;
const EVENTS_REFRESH_INTERVAL_MS = 2 * 60 * 1000;
/** A resume (focus, tab shown) refetches the events only when they are at least this old. */
const RESUME_REFETCH_GAP_MS = 60 * 1000;
const STATUS_RECHECK_INTERVAL_MS = 30 * 60 * 1000;
const BOUNDARY_TIMER_PADDING_MS = 50;
/** Recorded in user settings while this automation owns the status. */
const HOLD_SOURCE = 'calendar';
/** End of the meeting the user opted out of by hand, so a reload does not retake it. */
const OPT_OUT_STORAGE_KEY = 'konvoAutoPresenceOptOutUntil';

const MEETING_PRESET = KONVO_STATUS_PRESETS.find((preset) => preset.id === 'meeting')!;

/** Whether the calendar automation currently owns the user's status. */
function holdsStatus(): boolean {
	return UserSettings.getStatusHoldSource() === HOLD_SOURCE;
}

function sameInstant(a: string | null, b: string): boolean {
	if (a == null) return false;
	const parsed = Date.parse(a);
	return Number.isFinite(parsed) && parsed === Date.parse(b);
}

function readOptOutUntil(): number | null {
	try {
		const raw = getProtectedLocalStorage()?.getItem(OPT_OUT_STORAGE_KEY);
		const parsed = raw ? Number(raw) : Number.NaN;
		return Number.isFinite(parsed) && parsed > Date.now() ? parsed : null;
	} catch {
		return null;
	}
}

function writeOptOutUntil(until: number | null): void {
	try {
		if (until == null) getProtectedLocalStorage()?.removeItem(OPT_OUT_STORAGE_KEY);
		else getProtectedLocalStorage()?.setItem(OPT_OUT_STORAGE_KEY, String(until));
	} catch {
		// storage unavailable; the opt-out still holds for this session
	}
}

export class KonvoAutoPresence {
	private started = false;
	private epoch = 0;
	private disposeVisibility: (() => void) | null = null;
	private disposePreferences: (() => void) | null = null;
	private bridgeSupported: boolean | null = null;
	private readonly poll = new VisiblePoll(CHECK_INTERVAL_MS, () => this.requestTick(), true);
	private boundaryTimer: ReturnType<typeof setTimeout> | null = null;
	private events: Array<KonvoCalendarEvent> = [];
	private eventsFetchedAt = 0;
	private tickPromise: Promise<void> | null = null;
	private rerunRequested = false;
	private forceRefreshRequested = false;
	/** While set and in the future, a live meeting must not take over the status. */
	private optOutUntil: number | null = readOptOutUntil();
	private handingBack = false;
	private disposeHoldWatch: (() => void) | null = null;

	start(): void {
		if (this.started) return;
		this.started = true;
		this.epoch++;
		this.disposeHoldWatch = reaction(
			() => UserSettings.getStatusHoldSource(),
			(source, previous) => {
				if (previous === HOLD_SOURCE && source !== HOLD_SOURCE && !this.handingBack && !KonvoCalendarBridge.serverOwned)
					this.noteOptOut();
			},
		);
		this.disposeVisibility = reaction(
			() => Window.visible,
			(visible) => {
				this.epoch++;
				if (visible) {
					void this.requestTick(Date.now() - this.eventsFetchedAt >= RESUME_REFETCH_GAP_MS);
				} else {
					if (this.boundaryTimer) clearTimeout(this.boundaryTimer);
					this.boundaryTimer = null;
				}
			},
		);
		this.disposePreferences = reaction(
			() => JSON.stringify([KonvoCalendarPrefs.autoPresence, KonvoCalendarPrefs.calendarIds]),
			() => {
				KonvoCalendarBridge.reset();
				void this.requestTick(true);
			},
		);
		this.poll.start();
		if (this.tickPromise && Window.visible) void this.requestTick(true);
	}

	stop(): void {
		this.started = false;
		this.epoch++;
		this.poll.stop();
		if (this.boundaryTimer) clearTimeout(this.boundaryTimer);
		this.boundaryTimer = null;
		this.disposeHoldWatch?.();
		this.disposeHoldWatch = null;
		this.disposeVisibility?.();
		this.disposeVisibility = null;
		this.disposePreferences?.();
		this.disposePreferences = null;
		this.rerunRequested = false;
		this.forceRefreshRequested = false;
		this.events = [];
		this.eventsFetchedAt = 0;
		this.bridgeSupported = null;
		KonvoCalendarBridge.reset();
	}

	handleCalendarChanged(): void {
		this.eventsFetchedAt = 0;
		this.bridgeSupported = null;
		KonvoCalendarBridge.invalidate();
		void this.requestTick(true);
	}

	private active(epoch = this.epoch): boolean {
		return this.started && Window.visible && epoch === this.epoch;
	}

	private noteOptOut(): void {
		const meetingEnd = getLiveMeetingEnd(this.events);
		if (meetingEnd == null) return;
		this.optOutUntil = meetingEnd;
		writeOptOutUntil(meetingEnd);
	}

	private optedOut(now: number): boolean {
		if (this.optOutUntil == null) return false;
		if (now < this.optOutUntil) return true;
		this.optOutUntil = null;
		writeOptOutUntil(null);
		return false;
	}

	private requestTick(forceRefresh = false): Promise<void> {
		if (!this.active()) return Promise.resolve();
		this.forceRefreshRequested ||= forceRefresh;
		if (this.tickPromise) {
			this.rerunRequested = true;
			return this.tickPromise;
		}
		this.tickPromise = this.drainTicks()
			.catch(() => {
				// A calendar or settings request failed. Keep the current state and
				// retry on the next boundary, resume, or fallback interval.
			})
			.finally(() => {
				this.tickPromise = null;
				if (this.active() && (this.rerunRequested || this.forceRefreshRequested)) void this.requestTick();
			});
		return this.tickPromise;
	}

	private async drainTicks(): Promise<void> {
		do {
			this.rerunRequested = false;
			const forceRefresh = this.forceRefreshRequested;
			this.forceRefreshRequested = false;
			await this.tick(forceRefresh, this.epoch);
		} while (this.active() && this.rerunRequested);
	}

	private async tick(forceRefresh: boolean, epoch: number): Promise<void> {
		await KonvoCalendarPrefs.load();
		if (!this.active(epoch)) return;
		await this.refreshEventsIfStale(forceRefresh, epoch);
		if (!this.active(epoch) || KonvoCalendarBridge.serverOwned) return;
		const connected = await this.isConnected();
		if (!this.active(epoch)) return;
		if (!connected) {
			await this.restoreAfterMeeting();
			return;
		}
		// Events feed both auto-presence and the sidebar "up next" card, so the
		// fetch is not gated by the presence toggle.
		if (!KonvoCalendarPrefs.autoPresence) {
			await this.restoreAfterMeeting();
			return;
		}
		const now = Date.now();
		const meetingEnd = getLiveMeetingEnd(this.events, now);
		const holding = holdsStatus();
		if (meetingEnd && !holding) {
			// Only take over from a plain Available/online status; any other
			// status (Busy, Focus, OOO, invisible, ...) is a deliberate choice, and
			// so is Available chosen by hand during the current meeting.
			if (!this.optedOut(now) && UserSettings.status === StatusTypes.ONLINE && UserSettings.customStatus === null) {
				const autoUntil = new Date(meetingEnd).toISOString();
				const customStatus = presetCustomStatus(MEETING_PRESET);
				await UserSettingsCommands.update({
					status: MEETING_PRESET.status,
					customStatus: customStatus ? {...customStatus, expiresAt: autoUntil} : null,
					statusResetsAt: autoUntil,
					statusResetsTo: StatusTypes.ONLINE,
					statusHoldSource: HOLD_SOURCE,
				});
			}
		} else if (meetingEnd && holding) {
			// Back-to-back meetings extend the same hold.
			const autoUntil = new Date(meetingEnd).toISOString();
			if (!sameInstant(UserSettings.statusResetsAt, autoUntil)) {
				const customStatus = presetCustomStatus(MEETING_PRESET);
				await UserSettingsCommands.update({
					customStatus: customStatus ? {...customStatus, expiresAt: autoUntil} : null,
					statusResetsAt: autoUntil,
					statusResetsTo: StatusTypes.ONLINE,
					statusHoldSource: HOLD_SOURCE,
				});
			}
		} else if (!meetingEnd && holding) {
			await this.restoreAfterMeeting();
		}
		if (this.active(epoch)) this.scheduleNextBoundary();
	}

	private async restoreAfterMeeting(): Promise<void> {
		// Ownership is the only signal needed: the server drops it the moment the
		// user changes their own status, so reaching here means the status really
		// is still ours to hand back.
		if (!holdsStatus()) return;
		this.handingBack = true;
		try {
			await UserSettingsCommands.update({
				status: StatusTypes.ONLINE,
				customStatus: null,
				statusResetsAt: null,
				statusResetsTo: null,
				statusHoldSource: null,
			});
		} finally {
			this.handingBack = false;
		}
	}

	private scheduleNextBoundary(): void {
		if (this.boundaryTimer) clearTimeout(this.boundaryTimer);
		const now = Date.now();
		const boundary = getNextMeetingBoundary(this.events, now);
		if (boundary == null) {
			this.boundaryTimer = null;
			return;
		}
		this.boundaryTimer = setTimeout(
			() => {
				void this.requestTick(true);
			},
			Math.max(0, boundary - now) + BOUNDARY_TIMER_PADDING_MS,
		);
	}

	private async isConnected(): Promise<boolean> {
		return KonvoConnections.ensureMicrosoftStatus(STATUS_RECHECK_INTERVAL_MS);
	}

	private async refreshEventsIfStale(forceRefresh: boolean, epoch: number): Promise<void> {
		const now = Date.now();
		if (!forceRefresh && now - this.eventsFetchedAt < EVENTS_REFRESH_INTERVAL_MS) {
			return;
		}
		if (this.bridgeSupported !== false) {
			try {
				const response = await http.get<{
					enabled: boolean;
					connected?: boolean;
					server_owned: boolean;
					lease_expires_at?: string;
					events?: Array<KonvoCalendarEvent>;
				}>(Endpoints.KONVO_CALENDAR_PRESENCE);
				if (!this.active(epoch)) return;
				this.bridgeSupported = response.body?.enabled !== false;
				KonvoCalendarBridge.setOwnership(response.body?.server_owned === true, response.body?.lease_expires_at);
				if (response.body?.connected !== undefined) KonvoConnections.setMicrosoftConnected(response.body.connected);
				if (KonvoCalendarBridge.serverOwned) {
					this.events = response.body?.events ?? [];
					this.eventsFetchedAt = now;
					KonvoUpNext.setEvents(this.events);
					return;
				}
			} catch (error) {
				if (!this.active(epoch)) return;
				if ((error as {status?: number}).status === 404) this.bridgeSupported = false;
				// A transient failure does not revoke an acknowledged, unexpired lease.
				if (KonvoCalendarBridge.serverOwned) return;
			}
		}
		try {
			const start = new Date(now - 60 * 60 * 1000).toISOString();
			const end = new Date(now + 2 * 60 * 60 * 1000).toISOString();
			const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC';
			const params = new URLSearchParams({start, end, timezone});
			if (KonvoCalendarPrefs.calendarIds?.length) params.set('calendars', KonvoCalendarPrefs.calendarIds.join(','));
			const response = await http.get<{connected: boolean; events: Array<KonvoCalendarEvent>}>(
				`${Endpoints.KONVO_CALENDAR_EVENTS}?${params.toString()}`,
			);
			if (!this.active(epoch)) return;
			if (response.body?.connected != null) KonvoConnections.setMicrosoftConnected(response.body.connected);
			this.events = response.body?.events ?? [];
			this.eventsFetchedAt = now;
			KonvoUpNext.setEvents(this.events);
		} catch {
			// keep the previous window; retry on the next tick
		}
	}
}

export default new KonvoAutoPresence();
